import {
    takeEvery,
    all,
    call,
    put,
    select,
} from 'redux-saga/effects';
import actionFactory from '../../webcommon/header/filterheader/actions';
import { push, replace, LOCATION_CHANGE } from 'connected-react-router';
import * as filterHeaderSelectors from '../../webcommon/header/filterheader/selectors';
import fp from 'lodash/fp';
import queryString from 'query-string';
import urlJoin from 'url-join';
import * as actions from './actions';
import * as selectors from './selectors';
import { searchToFilters, filtersToSearch } from '../../common/urlUtils';
import * as appSelectors from '../../app/selectors';
import { getVirtualDirectory } from '../../common/helpermethods/HelperMethods';

const filterHeaderActions = actionFactory('ImCalculation');

export const PAGE_ROUTE = urlJoin(getVirtualDirectory(), '/summary/csadrilldown');

export function* filterChanged(action) {
    const initialised = yield select(selectors.getInitialised);
    if (initialised) {
        const currentlyOpenFilter = yield (select(filterHeaderSelectors.getCurrentlyOpenFilter, 'ImCalculation'));
        if (!currentlyOpenFilter) {
            const filters = yield select(filterHeaderSelectors.getFilters, 'ImCalculation');
            const search = filtersToSearch(filters);
            const sp = queryString.parse(search);
            let force = false;
            if (action && (
                action.type === actions.IM_CALCULATION_LOADED
            )) {
                sp.force = true;
                force = true;
            }
            const newSearch = `?${queryString.stringify(sp)}`;
            if (window.location.search === '' || force) {
                yield put(replace(`${PAGE_ROUTE}${newSearch}`));
            } else if (window.location.search !== newSearch) {
                yield put(push(`${PAGE_ROUTE}${newSearch}`));
            }
        }
    }
}

export function* imCalculationLoaded(action) {
    try {
        const initialised = yield (select(selectors.getInitialised));
        if (!initialised) {
            yield put(actions.setLoading(true));
            const filterDefinitions = yield select(selectors.getFilterDefinitions);
            yield put(filterHeaderActions.setFilterDefinitions(filterDefinitions));
            const defaultFilters = yield select(selectors.getDefaultFilters);
            yield put(filterHeaderActions.setDefaultFilters(defaultFilters));
            if (action.search === '') {
                yield put(filterHeaderActions.resetFilters());
            } else {
                const filters = searchToFilters(action.search, filterDefinitions, defaultFilters);
                yield put(filterHeaderActions.setFilters(filters));
            }
            yield put(actions.setInitialised(true));
            yield call(filterChanged, action);
        }
    } finally {
        yield put(actions.setLoading(false));
    }
}

export function* handleLocationChanged(action) {
    const initialised = yield (select(selectors.getInitialised));
    if (initialised) {
        try {
            if (action.payload.location.search === '') {
                // redirect to the page without query params,
                // probably from the navbar, so need to rebuild the url
                yield call(filterChanged);
            } else {
                const filterDefinitions = yield select(selectors.getFilterDefinitions);
                const defaultFilters = yield select(selectors.getDefaultFilters);
                const filters = searchToFilters(action.payload.location.search,
                    filterDefinitions, defaultFilters);
                yield put(filterHeaderActions.setFilters(filters));
                const params = queryString.parse(action.payload.location.search);
                const force = params.force === 'true';

                const requestedServerFilters = fp.flow(
                    fp.filter(filter => filter.value && filter.definition.server),
                    fp.sortBy(filter => filter.definition.id),
                    JSON.stringify
                )(filters);
                const currentServerFilters = yield select(selectors.getCurrentServerFilters);
                if (force || (currentServerFilters !== requestedServerFilters)) {
                    yield put(actions.setLoading(true));
                    // Need query here to server for data to load into grid
                    // const data = [1, 2, 3];
                    // yield put(actions.setRows(data, requestedServerFilters));
                }
            }
        } catch (error) {
            yield put(actions.fetchDataFailed(error.message));
        } finally {
            yield put(actions.setLoading(false));
        }
    }
}

export function* getAvailableValues() {
    const csaList = yield select(appSelectors.getCsas);
    yield put(filterHeaderActions.setAvailableListValues('csa', csaList));
}

export default function* rootSaga() {
    yield all([
        takeEvery(actions.IM_CALCULATION_LOADED, imCalculationLoaded),
        takeEvery([
            filterHeaderActions.UPDATE_FILTER,
            filterHeaderActions.REMOVE_FILTER,
            filterHeaderActions.ADD_FILTER,
            filterHeaderActions.RESET_FILTERS
        ], filterChanged),
        takeEvery(
            action => action.type === LOCATION_CHANGE
            && action.payload.location.pathname.toLowerCase() === PAGE_ROUTE.toLowerCase(),
            handleLocationChanged
        ),
        takeEvery(
            filterHeaderActions.FETCH_AVAILABLE_LIST_VALUES,
            getAvailableValues,
        )
    ]);
}
